// "Entrar na máquina" do acervo legado: passa foto a foto aprovando ou
// reprovando, para decidir se o material antigo se sustenta ou se é preciso
// voltar a campo tirar mais imagens.

import { useEffect, useMemo, useRef, useState } from 'react';
import {
  Check,
  ChevronLeft,
  ChevronRight,
  ImageOff,
  ImagePlus,
  Loader2,
  RotateCcw,
  SkipForward,
  Trash2,
  X,
} from 'lucide-react';
import { apiService } from '@/services/api';
import { useI18n } from '@/contexts/i18n-hook';
import type { RevisaoFotoStatus, UploadedFoto, UploadedMachineFolder } from '@/services/api';
import { toAbsoluteFotoUrl, withPreviewParams } from '@/utils/foto-url';
import { MOTIVOS_REPROVACAO, resumoRevisao, vereditoPasta } from './legado-revisao';

/**
 * Vídeo até esta duração é baixado assim que se entra na máquina, para já estar
 * pronto quando a revisão chegar nele. Acima disso o download só começa quando
 * o revisor der play — são arquivos de dezenas de MB.
 */
const SEGUNDOS_PRE_CARGA = 10;

/** Downloads simultâneos do adiantamento. Mais que isso trava o resto da tela. */
const PRE_CARGA_EM_PARALELO = 2;

interface Props {
  folder: UploadedMachineFolder;
  /** Projeto da pasta: necessário para consultar a duração dos vídeos. */
  projetoId?: string;
  /** Persiste o veredito. Retorna false quando a gravação falhou. */
  onRevisar: (fileName: string, status: RevisaoFotoStatus | null, motivo?: string) => Promise<boolean>;
  onFechar: () => void;
  onAdicionarFotos: () => void;
  onApagarFoto: (foto: UploadedFoto) => void;
  /** Nome do arquivo sendo apagado no momento (spinner na lixeira). */
  apagandoFoto?: string | null;
}

export function RevisaoLegadoModal({
  folder,
  projetoId,
  onRevisar,
  onFechar,
  onAdicionarFotos,
  onApagarFoto,
  apagandoFoto,
}: Props) {
  const { t, formatarDataHora } = useI18n();
  const fotos = folder.fotos;
  const [indice, setIndice] = useState(0);
  const [salvando, setSalvando] = useState(false);
  const [painelMotivo, setPainelMotivo] = useState(false);
  const [motivo, setMotivo] = useState('');
  const [erro, setErro] = useState('');
  const [preCarga, setPreCarga] = useState({ prontos: 0, total: 0 });
  const tirasRef = useRef<HTMLDivElement>(null);

  const resumo = useMemo(() => resumoRevisao(fotos), [fotos]);
  const veredito = useMemo(() => vereditoPasta(resumo), [resumo]);

  // A pasta é recarregada a cada veredito: mantém o índice dentro do intervalo
  // quando uma foto é apagada durante a revisão.
  const indiceSeguro = fotos.length === 0 ? 0 : Math.min(indice, fotos.length - 1);
  const fotoAtual: UploadedFoto | undefined = fotos[indiceSeguro];

  const irPara = (proximo: number) => {
    if (fotos.length === 0) return;
    const normalizado = (proximo + fotos.length) % fotos.length;
    setIndice(normalizado);
    setPainelMotivo(false);
    setMotivo('');
  };

  /** Depois de dar o veredito, o fluxo natural é cair na próxima foto. */
  const avancarAposVeredito = () => {
    const proximaPendente = fotos.findIndex((f, i) => i > indiceSeguro && !f.revisao);
    if (proximaPendente >= 0) setIndice(proximaPendente);
    else if (indiceSeguro < fotos.length - 1) setIndice(indiceSeguro + 1);
  };

  const pularParaPendente = () => {
    const idx = fotos.findIndex((f) => !f.revisao);
    if (idx >= 0) {
      setIndice(idx);
      setPainelMotivo(false);
    }
  };

  const registrar = async (status: RevisaoFotoStatus | null, motivoTexto?: string) => {
    if (!fotoAtual || salvando) return;
    setSalvando(true);
    setErro('');
    try {
      const ok = await onRevisar(fotoAtual.nomeArquivo, status, motivoTexto);
      if (!ok) {
        setErro(t('imagens.revisaoLegado.modal.erroSalvar'));
        return;
      }
      setPainelMotivo(false);
      setMotivo('');
      if (status) avancarAposVeredito();
    } finally {
      setSalvando(false);
    }
  };

  // Atalhos: ← → navegam, A aprova, R reprova, Esc fecha.
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      const alvo = event.target as HTMLElement | null;
      if (alvo && ['INPUT', 'TEXTAREA', 'SELECT'].includes(alvo.tagName)) {
        if (event.key === 'Escape') setPainelMotivo(false);
        return;
      }
      // Com o player em foco, ← → e espaço pertencem ao vídeo (buscar/pausar);
      // a navegação entre fotos continua nas setas da tela.
      if (alvo?.tagName === 'VIDEO' && ['ArrowRight', 'ArrowLeft', ' '].includes(event.key)) return;
      if (event.key === 'ArrowRight') irPara(indiceSeguro + 1);
      else if (event.key === 'ArrowLeft') irPara(indiceSeguro - 1);
      else if (event.key === 'Escape') {
        if (painelMotivo) setPainelMotivo(false);
        else onFechar();
      } else if (event.key === 'a' || event.key === 'A') void registrar('aprovada');
      else if (event.key === 'r' || event.key === 'R') setPainelMotivo(true);
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [indiceSeguro, fotos, painelMotivo, salvando]);

  // Adianta o download dos vídeos curtos assim que se entra na máquina, para
  // que a revisão não pare esperando buffer ao chegar em cada um. Só os curtos:
  // os longos são grandes demais para baixar sem alguém ter pedido.
  useEffect(() => {
    if (!projetoId) return;
    const videos = folder.fotos.filter((f) => f.tipo === 'video');
    if (videos.length === 0) return;

    const controller = new AbortController();
    let cancelado = false;

    void (async () => {
      const resposta = await apiService.duracoesVideosPasta(projetoId, folder.machineFolder);
      if (cancelado || !resposta.success || !resposta.data) return;

      const duracoes = resposta.data;
      const fila = videos.filter((v) => {
        const segundos = duracoes[v.nomeArquivo];
        return typeof segundos === 'number' && segundos < SEGUNDOS_PRE_CARGA;
      });
      if (fila.length === 0 || cancelado) return;
      setPreCarga({ prontos: 0, total: fila.length });

      const baixarDaFila = async () => {
        while (!cancelado) {
          const proximo = fila.shift();
          if (!proximo) return;
          try {
            // `no-cors` faz a requisição sair igual à do <video> (sem header
            // Origin), então o player reaproveita esta mesma entrada do cache
            // em vez de baixar o arquivo de novo.
            await fetch(toAbsoluteFotoUrl(proximo.url), {
              mode: 'no-cors',
              credentials: 'include',
              signal: controller.signal,
            });
          } catch {
            // Rede caiu ou o modal fechou: esse vídeo apenas não fica adiantado.
          }
          if (!cancelado) setPreCarga((atual) => ({ ...atual, prontos: atual.prontos + 1 }));
        }
      };

      await Promise.all(
        Array.from({ length: Math.min(PRE_CARGA_EM_PARALELO, fila.length) }, baixarDaFila)
      );
    })();

    return () => {
      cancelado = true;
      controller.abort();
    };
    // Roda uma vez por máquina: `folder.fotos` muda a cada veredito e não deve
    // reiniciar o adiantamento.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projetoId, folder.machineFolder]);

  // Mantém a miniatura corrente visível na tira lateral.
  useEffect(() => {
    const alvo = tirasRef.current?.querySelector<HTMLElement>(`[data-indice="${indiceSeguro}"]`);
    alvo?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
  }, [indiceSeguro]);

  const revisaoAtual = fotoAtual?.revisao;

  return (
    <div className="fixed inset-0 z-[60] bg-black/85 p-2 md:p-6 flex items-center justify-center">
      <div className="bg-white dark:bg-schmersal-gray-darker w-full max-w-7xl h-full max-h-[94vh] rounded-lg overflow-hidden flex flex-col">
        {/* Cabeçalho: máquina + veredito derivado */}
        <div className="p-3 md:p-4 border-b border-schmersal-gray-light dark:border-schmersal-gray-dark flex items-center gap-3">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2">
              <h3 className="font-bold text-base md:text-lg text-schmersal-gray-dark dark:text-white truncate">
                {folder.machineName}
              </h3>
              <span
                className={`shrink-0 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold ${veredito.badgeClass}`}
                title={t(veredito.detalheChave, veredito.detalheVars)}
              >
                <span className={`h-2 w-2 rounded-full ${veredito.dotClass}`} />
                {t(veredito.labelChave, veredito.labelVars)}
              </span>
            </div>
            <p className="text-xs text-schmersal-gray-medium mt-0.5">
              {folder.area ? `${folder.area} · ` : ''}
              {t('imagens.revisaoLegado.modal.resumo', {
                total: resumo.total,
                aprovadas: resumo.aprovadas,
                reprovadas: resumo.reprovadas,
                pendentes: resumo.pendentes,
              })}
              {preCarga.total > 0 && (
                <span className="ml-1">
                  ·{' '}
                  {preCarga.prontos < preCarga.total
                    ? t('imagens.revisaoLegado.modal.preCarga', {
                        prontos: preCarga.prontos,
                        total: preCarga.total,
                      })
                    : t('imagens.revisaoLegado.modal.preCargaPronta', { total: preCarga.total })}
                </span>
              )}
            </p>
          </div>

          {resumo.pendentes > 0 && (
            <button
              type="button"
              onClick={pularParaPendente}
              className="hidden md:inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-schmersal-gray-light dark:border-schmersal-gray-dark text-sm text-schmersal-gray-dark dark:text-schmersal-gray-light hover:border-schmersal-blue"
              title={t('imagens.revisaoLegado.modal.proximaPendenteTitulo')}
            >
              <SkipForward className="h-4 w-4" />
              {t('imagens.revisaoLegado.modal.proximaPendente')}
            </button>
          )}
          <button
            type="button"
            onClick={onAdicionarFotos}
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-schmersal-gray-light dark:border-schmersal-gray-dark text-sm text-schmersal-gray-dark dark:text-schmersal-gray-light hover:border-schmersal-blue"
            title={t('imagens.revisaoLegado.modal.adicionarFotosTitulo')}
          >
            <ImagePlus className="h-4 w-4" />
            <span className="hidden md:inline">{t('imagens.revisaoLegado.modal.adicionarFotos')}</span>
          </button>
          <button
            type="button"
            onClick={onFechar}
            className="p-2 rounded hover:bg-gray-100 dark:hover:bg-schmersal-gray-dark text-schmersal-gray-dark dark:text-white"
            title={t('imagens.revisaoLegado.modal.fechar')}
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {fotos.length === 0 ? (
          <div className="flex-1 flex flex-col items-center justify-center gap-2 text-schmersal-gray-medium">
            <ImageOff className="h-10 w-10 opacity-50" />
            <p className="text-sm">{t('imagens.revisaoLegado.modal.semFotos')}</p>
            <button
              type="button"
              onClick={onAdicionarFotos}
              className="mt-2 inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-schmersal-blue text-white text-sm"
            >
              <ImagePlus className="h-4 w-4" />
              {t('imagens.revisaoLegado.enviarFotosMigradas')}
            </button>
          </div>
        ) : (
          <div className="flex-1 min-h-0 flex flex-col lg:flex-row">
            {/* Visor */}
            <div className="flex-1 min-w-0 flex flex-col bg-schmersal-gray-darkest/95">
              <div className="relative flex-1 min-h-0 flex items-center justify-center p-3">
                {/* Vídeo precisa de <video>: num <img> o .mov baixa inteiro e
                    não mostra nada. O poster é a mesma miniatura da tira. */}
                {fotoAtual && fotoAtual.tipo === 'video' ? (
                  <video
                    key={fotoAtual.nomeArquivo}
                    src={toAbsoluteFotoUrl(fotoAtual.url)}
                    poster={withPreviewParams(toAbsoluteFotoUrl(fotoAtual.previewUrl), 900, 70)}
                    controls
                    preload="metadata"
                    className="max-w-full max-h-full object-contain"
                  />
                ) : fotoAtual ? (
                  <img
                    key={fotoAtual.nomeArquivo}
                    src={toAbsoluteFotoUrl(fotoAtual.url)}
                    alt={fotoAtual.nomeArquivo}
                    className="max-w-full max-h-full object-contain"
                  />
                ) : null}
                <button
                  type="button"
                  onClick={() => irPara(indiceSeguro - 1)}
                  className="absolute left-3 top-1/2 -translate-y-1/2 p-2 rounded-full bg-black/50 text-white hover:bg-black/70"
                  title={t('imagens.revisaoLegado.modal.fotoAnterior')}
                >
                  <ChevronLeft className="h-6 w-6" />
                </button>
                <button
                  type="button"
                  onClick={() => irPara(indiceSeguro + 1)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 p-2 rounded-full bg-black/50 text-white hover:bg-black/70"
                  title={t('imagens.revisaoLegado.modal.proximaFoto')}
                >
                  <ChevronRight className="h-6 w-6" />
                </button>
                <div className="absolute top-3 left-3 px-2.5 py-1 rounded-full bg-black/60 text-white text-xs font-medium">
                  {indiceSeguro + 1} / {fotos.length} · {fotoAtual?.nomeArquivo}
                </div>
                {revisaoAtual && (
                  <div
                    className={`absolute top-3 right-3 px-2.5 py-1 rounded-full text-xs font-semibold ${
                      revisaoAtual.status === 'aprovada'
                        ? 'bg-emerald-500 text-white'
                        : 'bg-rose-500 text-white'
                    }`}
                    title={
                      [revisaoAtual.motivo, revisaoAtual.revisadoPor, formatarDataHora(revisaoAtual.revisadoEm)]
                        .filter(Boolean)
                        .join(' · ')
                    }
                  >
                    {revisaoAtual.status === 'aprovada'
                      ? t('imagens.revisaoLegado.modal.aprovada')
                      : t('imagens.revisaoLegado.modal.reprovada')}
                    {revisaoAtual.motivo
                      ? t('imagens.revisaoLegado.modal.motivoSufixo', { motivo: revisaoAtual.motivo })
                      : ''}
                  </div>
                )}
              </div>

              {/* Ações do veredito */}
              <div className="border-t border-white/10 p-3 space-y-2">
                {erro && <p className="text-xs text-rose-300">{erro}</p>}
                {painelMotivo && (
                  <div className="rounded-lg bg-white dark:bg-schmersal-gray-darker p-3 space-y-2">
                    <p className="text-xs font-semibold text-schmersal-gray-medium uppercase tracking-wide">
                      {t('imagens.revisaoLegado.modal.porQueReprovada')}
                    </p>
                    <div className="flex flex-wrap gap-1.5">
                      {MOTIVOS_REPROVACAO.map((preset) => (
                        <button
                          key={preset.valor}
                          type="button"
                          onClick={() => registrar('reprovada', preset.valor)}
                          disabled={salvando}
                          className="px-2.5 py-1 rounded-full border border-rose-300 text-rose-700 text-xs hover:bg-rose-50 disabled:opacity-50"
                        >
                          {t(preset.chave)}
                        </button>
                      ))}
                    </div>
                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={motivo}
                        autoFocus
                        onChange={(e) => setMotivo(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') void registrar('reprovada', motivo.trim() || undefined);
                        }}
                        placeholder={t('imagens.revisaoLegado.modal.outroMotivo')}
                        className="flex-1 min-w-0 px-3 py-2 text-sm border rounded-lg bg-white dark:bg-schmersal-gray-darkest"
                      />
                      <button
                        type="button"
                        onClick={() => registrar('reprovada', motivo.trim() || undefined)}
                        disabled={salvando}
                        className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-rose-600 text-white text-sm disabled:opacity-50"
                      >
                        {salvando ? <Loader2 className="h-4 w-4 animate-spin" /> : <X className="h-4 w-4" />}
                        {t('imagens.revisaoLegado.modal.reprovar')}
                      </button>
                      <button
                        type="button"
                        onClick={() => setPainelMotivo(false)}
                        className="px-3 py-2 rounded-lg border text-sm text-schmersal-gray-dark dark:text-schmersal-gray-light"
                      >
                        {t('comum.acao.cancelar')}
                      </button>
                    </div>
                  </div>
                )}

                <div className="flex flex-wrap items-center gap-2">
                  <button
                    type="button"
                    onClick={() => registrar('aprovada')}
                    disabled={salvando}
                    className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-semibold disabled:opacity-50 ${
                      revisaoAtual?.status === 'aprovada'
                        ? 'bg-emerald-600 text-white'
                        : 'bg-white text-emerald-700 border border-emerald-400 hover:bg-emerald-50'
                    }`}
                    title={t('imagens.revisaoLegado.modal.aprovarTitulo')}
                  >
                    {salvando ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
                    {t('imagens.revisaoLegado.modal.aprovar')}
                  </button>
                  <button
                    type="button"
                    onClick={() => setPainelMotivo(true)}
                    disabled={salvando}
                    className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-semibold disabled:opacity-50 ${
                      revisaoAtual?.status === 'reprovada'
                        ? 'bg-rose-600 text-white'
                        : 'bg-white text-rose-700 border border-rose-400 hover:bg-rose-50'
                    }`}
                    title={t('imagens.revisaoLegado.modal.reprovarTitulo')}
                  >
                    <X className="h-4 w-4" />
                    {t('imagens.revisaoLegado.modal.reprovar')}
                  </button>
                  {revisaoAtual && (
                    <button
                      type="button"
                      onClick={() => registrar(null)}
                      disabled={salvando}
                      className="inline-flex items-center gap-2 px-3 py-2.5 rounded-lg border border-white/30 text-white text-sm hover:bg-white/10 disabled:opacity-50"
                      title={t('imagens.revisaoLegado.modal.desfazerTitulo')}
                    >
                      <RotateCcw className="h-4 w-4" />
                      {t('imagens.revisaoLegado.modal.desfazer')}
                    </button>
                  )}
                  {fotoAtual && (
                    <button
                      type="button"
                      onClick={() => onApagarFoto(fotoAtual)}
                      disabled={apagandoFoto === fotoAtual.nomeArquivo}
                      className="ml-auto inline-flex items-center gap-2 px-3 py-2.5 rounded-lg border border-rose-400/60 text-rose-300 text-sm hover:bg-rose-500/10 disabled:opacity-50"
                      title={t('imagens.revisaoLegado.modal.apagarTitulo')}
                    >
                      {apagandoFoto === fotoAtual.nomeArquivo ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <Trash2 className="h-4 w-4" />
                      )}
                      {t('imagens.revisaoLegado.modal.apagar')}
                    </button>
                  )}
                </div>
                <p className="text-[11px] text-white/50">
                  {t('imagens.revisaoLegado.modal.atalhos')}
                </p>
              </div>
            </div>

            {/* Tira de miniaturas com o veredito de cada foto */}
            <div
              ref={tirasRef}
              className="lg:w-56 shrink-0 border-t lg:border-t-0 lg:border-l border-schmersal-gray-light dark:border-schmersal-gray-dark overflow-auto p-2 flex lg:block gap-2"
            >
              {fotos.map((foto, idx) => {
                const status = foto.revisao?.status;
                return (
                  <button
                    key={foto.nomeArquivo}
                    type="button"
                    data-indice={idx}
                    onClick={() => irPara(idx)}
                    className={`relative shrink-0 w-28 lg:w-full mb-0 lg:mb-2 rounded overflow-hidden border-2 ${
                      idx === indiceSeguro ? 'border-schmersal-blue' : 'border-transparent'
                    }`}
                    title={foto.revisao?.motivo || foto.nomeArquivo}
                  >
                    <img
                      src={withPreviewParams(toAbsoluteFotoUrl(foto.previewUrl), 240, 45)}
                      alt={foto.nomeArquivo}
                      loading="lazy"
                      className={`w-full h-20 object-cover ${status === 'reprovada' ? 'opacity-50' : ''}`}
                    />
                    <span
                      className={`absolute top-1 right-1 h-4 w-4 rounded-full flex items-center justify-center text-white ${
                        status === 'aprovada'
                          ? 'bg-emerald-500'
                          : status === 'reprovada'
                          ? 'bg-rose-500'
                          : 'bg-gray-400/90'
                      }`}
                    >
                      {status === 'aprovada' ? (
                        <Check className="h-3 w-3" />
                      ) : status === 'reprovada' ? (
                        <X className="h-3 w-3" />
                      ) : null}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
